import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrg } from "@/hooks/use-current-org";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { OrgSwitcher } from "@/components/org-switcher";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/contacts")({
  component: ContactsPage,
});

function ContactsPage() {
  const { orgId } = useCurrentOrg();
  const [rows, setRows] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", county: "", constituency: "", ward: "" });

  async function load() {
    if (!orgId) return;
    let query = supabase.from("contacts").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }).limit(200);
    if (q) query = query.or(`first_name.ilike.%${q}%,last_name.ilike.%${q}%,phone.ilike.%${q}%,email.ilike.%${q}%`);
    const { data } = await query;
    setRows(data ?? []);
  }
  useEffect(() => { load(); }, [orgId, q]);

  async function save() {
    if (!orgId) return toast.error("Select an organization first");
    const { error } = await supabase.from("contacts").insert({ ...form, organization_id: orgId });
    if (error) return toast.error(error.message);
    toast.success("Contact added");
    setOpen(false); setForm({ first_name: "", last_name: "", phone: "", email: "", county: "", constituency: "", ward: "" });
    load();
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Contacts</h1>
          <p className="text-muted-foreground text-sm mt-1">Your audience database.</p>
        </div>
        <div className="flex items-center gap-2">
          <OrgSwitcher />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Add contact</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add contact</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {(["first_name","last_name","phone","email","county","constituency","ward"] as const).map((k) => (
                  <div key={k} className={k === "phone" || k === "email" ? "" : ""}>
                    <Label className="capitalize">{k.replace("_", " ")}</Label>
                    <Input value={(form as any)[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                  </div>
                ))}
              </div>
              <Button onClick={save} disabled={!form.phone && !form.email}>Save</Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search by name, phone, email" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Email</TableHead>
                <TableHead>County</TableHead><TableHead>Ward</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}</TableCell>
                  <TableCell>{r.phone || "—"}</TableCell>
                  <TableCell>{r.email || "—"}</TableCell>
                  <TableCell>{r.county || "—"}</TableCell>
                  <TableCell>{r.ward || "—"}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-12">No contacts yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
